import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Shell } from '../../components/layout/Shell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Buildings, User } from '@phosphor-icons/react';

const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get('role') === 'employer' ? 'employer' : 'candidate');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { email, password, name, role };
      if (role === 'employer') payload.company_name = company || name;
      const u = await register(payload);
      toast.success(`Welcome to Hikari, ${u.name}`);
      nav(u.role === 'employer' ? '/employer' : '/candidate');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className="label-eyebrow text-primary mb-4">JOIN HIKARI</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-6">
              Two sides. One standard: proof.
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              Choose how you'll use Hikari. You can always add the other role later from your account.
            </p>
          </div>

          <div className="lg:col-span-3">
            {/* Role selector */}
            <div className="grid grid-cols-2 gap-0 grid-skeleton mb-6" data-testid="role-selector">
              <button
                type="button"
                onClick={() => setRole('candidate')}
                className={`p-6 text-left transition-colors ${role === 'candidate' ? 'bg-foreground text-background' : 'bg-white hover:bg-secondary/50'}`}
                data-testid="role-candidate"
              >
                <User size={24} weight="duotone" className="mb-3" />
                <div className="font-display font-black text-lg">Candidate</div>
                <div className="text-xs mt-1 opacity-70">Show your work. Get ranked by capability.</div>
              </button>
              <button
                type="button"
                onClick={() => setRole('employer')}
                className={`p-6 text-left transition-colors ${role === 'employer' ? 'bg-foreground text-background' : 'bg-white hover:bg-secondary/50'}`}
                data-testid="role-employer"
              >
                <Buildings size={24} weight="duotone" className="mb-3" />
                <div className="font-display font-black text-lg">Employer</div>
                <div className="text-xs mt-1 opacity-70">See a ranked shortlist with transparent proof.</div>
              </button>
            </div>

            <form onSubmit={submit} className="border border-border bg-white p-8 space-y-5" data-testid="register-form">
              <div>
                <Label className="label-eyebrow text-muted-foreground">{role === 'employer' ? 'Your name' : 'Full name'}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-2 rounded-none h-11" data-testid="register-name" />
              </div>
              {role === 'employer' && (
                <div>
                  <Label className="label-eyebrow text-muted-foreground">Company name</Label>
                  <Input value={company} onChange={(e) => setCompany(e.target.value)} className="mt-2 rounded-none h-11" data-testid="register-company" />
                </div>
              )}
              <div>
                <Label className="label-eyebrow text-muted-foreground">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 rounded-none h-11" data-testid="register-email" />
              </div>
              <div>
                <Label className="label-eyebrow text-muted-foreground">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-2 rounded-none h-11" data-testid="register-password" />
                <div className="text-xs text-muted-foreground mt-1">Minimum 6 characters.</div>
              </div>
              <Button type="submit" disabled={loading} className="rounded-none w-full h-11" data-testid="register-submit">
                {loading ? 'Creating account...' : `Create ${role} account`}
              </Button>
              <div className="text-sm text-muted-foreground text-center">
                Already have an account? <Link to="/login" className="text-primary font-medium" data-testid="register-to-login">Sign in</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default Register;
